import { computePromotionDiscount } from './promotionSchedule';

export type PromoLimitFields = {
  id?: string;
  budget_total_jod?: number | null;
  budget_spent_jod?: number | null;
  max_uses?: number | null;
  max_uses_per_customer?: number | null;
  redemption_count?: number | null;
  customer_redemption_count?: number | null;
  usage_known?: boolean;
  checkout_blocked_reason?: string | null;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  promotion_type?: string | null;
  deal_type?: string | null;
  bogo_off_percent?: number | null;
  offer_applies_to?: string | null;
};

function hasCheckoutCap(promo: PromoLimitFields): boolean {
  const budget = Number(promo.budget_total_jod);
  const maxUses = Number(promo.max_uses);
  const maxPerCustomer = Number(promo.max_uses_per_customer);
  return (
    (Number.isFinite(budget) && budget > 0) ||
    (Number.isFinite(maxUses) && maxUses > 0) ||
    (Number.isFinite(maxPerCustomer) && maxPerCustomer > 0)
  );
}

function redemptionCapReason(promo: PromoLimitFields): string | null {
  const maxUses = Number(promo.max_uses);
  const maxPerCustomer = Number(promo.max_uses_per_customer);
  const totalUses = Number(promo.redemption_count) || 0;
  const customerUses = Number(promo.customer_redemption_count) || 0;
  if (Number.isFinite(maxUses) && maxUses > 0 && totalUses >= maxUses) {
    return 'This promotion has reached its limit';
  }
  if (Number.isFinite(maxPerCustomer) && maxPerCustomer > 0 && customerUses >= maxPerCustomer) {
    return 'You have already used this promotion';
  }
  return null;
}

function budgetBlocksDiscount(promo: PromoLimitFields, discount: number): boolean {
  const budget = Number(promo.budget_total_jod);
  if (!Number.isFinite(budget) || budget <= 0) return false;
  const spent = Math.max(0, Number(promo.budget_spent_jod) || 0);
  const next = Math.max(0, Number(discount) || 0);
  return spent + next > budget + 0.0001;
}

/** Reason to skip auto-apply. Null means this promo may be selected. */
export function promoBlockedForAutoApply(
  promo: PromoLimitFields,
  subtotal: number,
  items?: Array<{ price?: number; quantity?: number; category?: string; name?: string }> | null
): string | null {
  if (promo.checkout_blocked_reason) return String(promo.checkout_blocked_reason);
  if (promo.usage_known === false && hasCheckoutCap(promo)) {
    return 'This promotion cannot be applied right now';
  }
  const capped = redemptionCapReason(promo);
  if (capped) return capped;
  const discount = computePromotionDiscount(subtotal, promo as never, items as never);
  if (budgetBlocksDiscount(promo, discount)) return 'This promotion has reached its budget';
  return null;
}

export function isPromotionCheckoutError(message: string): boolean {
  const text = String(message || '').toLowerCase();
  return text.includes('promotion') || text.includes('already used this');
}
